import { secretFetch as fetch, useSecretBroker, secretIdLabel, hasSecretCredentials } from "../components/secret-broker/credentials";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronDown, Download, RefreshCw, Save } from "lucide-react";

const API_BASE = window.location.hostname === "localhost" ? "http://localhost:5000" : "";
const INDEXES = ["ALL", "NIFTY", "BANKNIFTY", "SENSEX", "BANKEX"];
const HOURLY_PERIODS = [
  "09:15 - 10:15",
  "10:15 - 11:15",
  "11:15 - 12:15",
  "12:15 - 13:15",
  "13:15 - 14:15",
  "14:15 - 15:15",
  "15:15 - 15:45",
];

const LEVERAGE_RULES = {
  NIFTY: {
    atm: 8.9,
    itmDecreasePerStep: 0.17,
    otmFirstIncrease: 0.18,
    otmIncreaseStep: 0.02,
  },
  BANKNIFTY: {
    atm: 8.9,
    itmDecreasePerStep: 0.11,
    otmFirstIncrease: 0.11,
    otmIncreaseStep: 0.01,
  },
  SENSEX: {
    atm: 8.9,
    itmDecreasePerStep: 0.11,
    otmFirstIncrease: 0.11,
    otmIncreaseStep: 0.01,
  },
  BANKEX: {
    atm: 8.9,
    itmDecreasePerStep: 0.11,
    otmFirstIncrease: 0.11,
    otmIncreaseStep: 0.01,
  },
};

const colors = {
  background: "#020202",
  panel: "#070604",
  card: "#0b0905",
  gold: "#f7c948",
  dimGold: "#9f7a24",
  green: "#22c55e",
  red: "#ef4444",
  blue: "#38bdf8",
  line: "rgba(247,201,72,0.26)",
};

function number(value) {
  return Number(value || 0);
}

function fmt(value, digits = 0) {
  return number(value).toLocaleString("en-IN", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function strikeInterval(snapshot) {
  const strikes = [...new Set((snapshot?.strikes || []).map(row => number(row.strike)).filter(Boolean))]
    .sort((a, b) => a - b);
  const intervals = strikes
    .slice(1)
    .map((strike, position) => strike - strikes[position])
    .filter(interval => interval > 0);
  return intervals.length ? Math.min(...intervals) : 1;
}

function leverageForStrike(snapshot, side, strike) {
  const rules = LEVERAGE_RULES[snapshot?.index] || LEVERAGE_RULES.SENSEX;
  const atm = number(snapshot?.atmStrike);
  const interval = strikeInterval(snapshot);
  const steps = Math.max(0, Math.round(Math.abs(number(strike) - atm) / interval));
  if (!steps) return rules.atm;

  const isCall = side === "ce";
  const isItm = isCall ? number(strike) < atm : number(strike) > atm;
  if (isItm) {
    return Math.max(0.1, rules.atm - steps * rules.itmDecreasePerStep);
  }

  const cumulativeOtmIncrease =
    steps * rules.otmFirstIncrease
    + (rules.otmIncreaseStep * steps * (steps - 1)) / 2;
  return rules.atm + cumulativeOtmIncrease;
}

function strategyStrikeRows(snapshot, strategyKey) {
  const strikeSet = new Set((snapshot?.strategy?.[strategyKey] || []).map(number));
  return (snapshot?.strikes || []).filter(row => strikeSet.has(number(row.strike)));
}

function sellerMargin(snapshot, side) {
  if (!snapshot) return 0;
  const strategyKey = side === "ce" ? "ceSellerStrikes" : "peSellerStrikes";
  return strategyStrikeRows(snapshot, strategyKey).reduce((total, row) => {
    const strike = number(row.strike);
    const quantity = number(row[side]?.oi);
    return total + (strike * quantity) / leverageForStrike(snapshot, side, strike);
  }, 0);
}

function sellerMarginChange(open, close, side) {
  if (!open || !close) return 0;
  const quantityField = side === "ce" ? "ceWritingOi" : "peWritingOi";
  const totalQuantity = number(close[quantityField]);
  const quantityChange = totalQuantity - number(open[quantityField]);
  const averageMarginPerUnit = totalQuantity ? sellerMargin(close, side) / totalQuantity : 0;
  return quantityChange * averageMarginPerUnit;
}

function nearestPremium(snapshot, side, strike, priceSnapshots) {
  const direct = (snapshot?.strikes || []).find(row => number(row.strike) === number(strike))?.[side];
  if (number(direct?.ltp) > 0) return number(direct.ltp);

  const snapshotTime = new Date(snapshot?.capturedAt).getTime();
  return (priceSnapshots || [])
    .filter(record =>
      record.index === snapshot?.index
      && record.date === snapshot?.date
      && record.expiry === snapshot?.expiry
    )
    .map(record => {
      const option = (record.strikes || []).find(row => number(row.strike) === number(strike))?.[side];
      return {
        premium: number(option?.ltp),
        distance: Math.abs(new Date(record.capturedAt).getTime() - snapshotTime),
      };
    })
    .filter(candidate => candidate.premium > 0)
    .sort((a, b) => a.distance - b.distance)[0]?.premium || 0;
}

function volumeFunds(snapshot, side, priceSnapshots = []) {
  if (!snapshot) return 0;
  const strategyKey = side === "ce" ? "ceItmStrikes" : "peItmStrikes";
  return strategyStrikeRows(snapshot, strategyKey).reduce((total, row) =>
    total + nearestPremium(snapshot, side, row.strike, priceSnapshots) * number(row[side]?.volume)
  , 0);
}

function volumeFundsChange(open, close, side, priceSnapshots = []) {
  if (!open || !close) return 0;
  const totalVolume = number(close[side === "ce" ? "ceVolume" : "peVolume"]);
  const quantityChange =
    totalVolume - number(open[side === "ce" ? "ceVolume" : "peVolume"]);
  const weightedPremium = totalVolume ? volumeFunds(close, side, priceSnapshots) / totalVolume : 0;
  return quantityChange * weightedPremium;
}

function localDate(date = new Date()) {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

function addDays(date, days) {
  const next = new Date(`${date}T12:00:00+05:30`);
  next.setDate(next.getDate() + days);
  return localDate(next);
}

function weekRange(offset = 0) {
  const now = new Date();
  const indiaDate = new Date(now.toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
  const mondayOffset = (indiaDate.getDay() + 6) % 7;
  indiaDate.setDate(indiaDate.getDate() - mondayOffset + offset * 7);
  const start = localDate(indiaDate);
  return { from: start, to: addDays(start, 6) };
}

function zoneText(zones) {
  return (zones || []).map(zone => `${fmt(zone.strike)} (${fmt(zone.oi)})`).join(" | ") || "--";
}

function periodStudy(records, date, index, period) {
  const snapshots = records
    .filter(row => row.date === date && row.index === index && row.period === period)
    .sort((a, b) => new Date(a.capturedAt) - new Date(b.capturedAt));
  if (!snapshots.length) return null;

  const first = snapshots[0];
  const last = snapshots[snapshots.length - 1];
  const move = number(last.spot) - number(first.spot);
  const ceWritingAdded = snapshots.reduce((sum, row) => sum + number(row.ceWritingAdded), 0);
  const peWritingAdded = snapshots.reduce((sum, row) => sum + number(row.peWritingAdded), 0);
  const ceBuyingAdded = snapshots.reduce((sum, row) => sum + number(row.ceBuyingAdded), 0);
  const peBuyingAdded = snapshots.reduce((sum, row) => sum + number(row.peBuyingAdded), 0);
  let impact = "MIXED / ROTATIONAL";

  if (peWritingAdded > ceWritingAdded && move > 0) impact = "PUT WRITERS DEFENDED SUPPORT";
  else if (peWritingAdded > ceWritingAdded && move < 0) impact = "PUT SUPPORT FAILED";
  else if (ceWritingAdded > peWritingAdded && move < 0) impact = "CALL WRITERS DEFENDED RESISTANCE";
  else if (ceWritingAdded > peWritingAdded && move > 0) impact = "CALL RESISTANCE ABSORBED";
  else if (ceBuyingAdded > peBuyingAdded && move > 0) impact = "CALL BUYERS DROVE UPSIDE";
  else if (peBuyingAdded > ceBuyingAdded && move < 0) impact = "PUT BUYERS DROVE DOWNSIDE";

  return {
    snapshots,
    first,
    last,
    open: first.spot,
    high: Math.max(...snapshots.map(row => number(row.spot))),
    low: Math.min(...snapshots.map(row => number(row.spot))),
    close: last.spot,
    move,
    ceWritingAdded,
    peWritingAdded,
    ceBuyingAdded,
    peBuyingAdded,
    ceOiChange: number(last.ceOi) - number(first.ceOi),
    peOiChange: number(last.peOi) - number(first.peOi),
    ceVolumeChange: Math.max(0, number(last.ceVolume) - number(first.ceVolume)),
    peVolumeChange: Math.max(0, number(last.peVolume) - number(first.peVolume)),
    impact,
  };
}

function timeToMinutes(value) {
  const [hour, minute] = String(value || "00:00").split(":").map(Number);
  return hour * 60 + minute;
}

function minuteLabel(totalMinutes) {
  const hour = Math.floor(totalMinutes / 60);
  const minute = totalMinutes % 60;
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

function candleReason(open, close, deltas) {
  const move = number(close.spot) - number(open.spot);
  const candle = move > 0 ? "BULLISH" : move < 0 ? "BEARISH" : "FLAT";
  const drivers = [
    { value: deltas.peSellers, text: deltas.peSellers >= 0 ? "PE sellers added" : "PE sellers decreased" },
    { value: -deltas.ceSellers, text: deltas.ceSellers <= 0 ? "CE sellers decreased" : "CE sellers added" },
    { value: deltas.ceVolume, text: "CE volume added" },
    { value: -deltas.peVolume, text: "PE volume added" },
  ];
  const expectedDirection = move >= 0 ? 1 : -1;
  const ranked = drivers
    .map(driver => ({ ...driver, agreement: driver.value * expectedDirection }))
    .sort((a, b) => b.agreement - a.agreement);
  const reason = ranked[0]?.agreement > 0
    ? ranked[0].text
    : deltas.peSellers > deltas.ceSellers
      ? "PE writing stronger but price diverged"
      : "CE writing stronger but price diverged";
  return { candle, reason, move: Number(move.toFixed(2)) };
}

function buildIntervalLedger(records, date, index, period, intervalMinutes = 1) {
  const [startText, endText] = period.split(" - ");
  const start = timeToMinutes(startText);
  const end = timeToMinutes(endText);
  const dayRecords = records
    .filter(row => row.date === date && row.index === index)
    .sort((a, b) => new Date(a.capturedAt) - new Date(b.capturedAt));
  const byMinute = new Map();

  dayRecords.forEach(record => {
    const key = String(record.time || "").slice(0, 5);
    byMinute.set(key, record);
  });

  return Array.from(
    { length: Math.ceil((end - start) / intervalMinutes) },
    (_, position) => {
      const minuteStart = start + position * intervalMinutes;
      const minuteEnd = Math.min(minuteStart + intervalMinutes, end);
      const open = byMinute.get(minuteLabel(minuteStart));
      const close = byMinute.get(minuteLabel(minuteEnd));
      const base = {
        serial: position + 1,
        label: `${minuteLabel(minuteStart)} - ${minuteLabel(minuteEnd)}`,
        open,
        close,
      };

      if (!open || !close) return base;

      const deltas = {
        ceSellers: number(close.ceWritingOi) - number(open.ceWritingOi),
        peSellers: number(close.peWritingOi) - number(open.peWritingOi),
        ceVolume: number(close.ceVolume) - number(open.ceVolume),
        peVolume: number(close.peVolume) - number(open.peVolume),
        ceOi: number(close.ceOi) - number(open.ceOi),
        peOi: number(close.peOi) - number(open.peOi),
      };

      return { ...base, deltas, ...candleReason(open, close, deltas) };
    }
  );
}

function buildMinuteLedger(records, date, index, period) {
  return buildIntervalLedger(records, date, index, period, 1);
}

function csvCell(value) {
  return `"${String(value ?? "").replace(/"/g, '""')}"`;
}

function SummaryCard({ label, value, color = colors.gold, detail }) {
  return (
    <div style={{ background: colors.card, border: `1px solid ${colors.line}`, borderRadius: 8, padding: 14 }}>
      <div style={{ color: colors.dimGold, fontSize: 11, fontWeight: 800 }}>{label}</div>
      <div style={{ color, fontSize: 21, fontWeight: 900, marginTop: 5 }}>{value}</div>
      {detail && <div style={{ color: colors.dimGold, fontSize: 11, marginTop: 4 }}>{detail}</div>}
    </div>
  );
}

function StudyMetric({ label, value, color, definition, funds, fundsLabel }) {
  return (
    <div title={definition} style={{ minWidth: 165, padding: "7px 9px", background: colors.card, border: `1px solid ${colors.line}`, borderRadius: 5 }}>
      <div style={{ color: colors.dimGold, fontSize: 8, letterSpacing: "0.08em" }}>{label}</div>
      <div style={{ color, fontSize: 13, fontWeight: 900, marginTop: 3 }}>{value}</div>
      {funds !== undefined && (
        <div style={{ color: colors.dimGold, fontSize: 8, lineHeight: 1.25, marginTop: 3 }}>
          (₹{fmt(number(funds) / 10000000, 2)} Cr)
          <div>{fundsLabel}</div>
        </div>
      )}
    </div>
  );
}

function CroreDetail({ value, change = false, changeQuantity = 0, label = "total margin" }) {
  const amount = number(value);
  const quantity = number(changeQuantity);

  const action = change
    ? quantity > 0
      ? "added"
      : quantity < 0
        ? "left"
        : "unchanged"
    : label;

  return (
    <div style={{ color: colors.dimGold, fontSize: 8, lineHeight: 1.25, marginTop: 3 }}>
      ({amount < 0 ? "-" : ""}₹{fmt(Math.abs(amount) / 10000000, 2)} Cr)
      <div>{action}</div>
    </div>
  );
}

function signed(value) {
  const next = number(value);
  return `${next >= 0 ? "+" : ""}${fmt(next)}`;
}

function volumeRowHighlight(row, intervalMinutes) {
  if (!row?.deltas) return "";

  const threshold = intervalMinutes === 5 ? 200000 : 30000;
  const ceChange = number(row.deltas.ceVolume);
  const peChange = number(row.deltas.peVolume);

  const bullishStrength = Math.max(
    ceChange >= threshold ? ceChange : 0,
    peChange <= -threshold ? Math.abs(peChange) : 0
  );

  const bearishStrength = Math.max(
    peChange >= threshold ? peChange : 0,
    ceChange <= -threshold ? Math.abs(ceChange) : 0
  );

  if (!bullishStrength && !bearishStrength) return "";
  if (bullishStrength === bearishStrength) return "";

  return bullishStrength > bearishStrength
    ? "rgba(34, 197, 94, 0.24)"
    : "rgba(239, 68, 68, 0.24)";
}

function volumeHighlightField(row, intervalMinutes) {
  if (!row?.deltas) return "";

  const threshold = intervalMinutes === 5 ? 200000 : 30000;
  const ce = number(row.deltas.ceVolume);
  const pe = number(row.deltas.peVolume);

  const signals = [
    { field: "ce", strength: ce >= threshold ? ce : 0 },
    { field: "pe", strength: pe >= threshold ? pe : 0 },
    { field: "ce", strength: ce <= -threshold ? Math.abs(ce) : 0 },
    { field: "pe", strength: pe <= -threshold ? Math.abs(pe) : 0 },
  ]
    .filter(signal => signal.strength > 0)
    .sort((a, b) => b.strength - a.strength);

  if (!signals.length) return "";
  if (signals[1]?.strength === signals[0].strength) return "";

  return signals[0].field;
}

function sellerConfirmationField(row, intervalMinutes) {
  if (!row?.deltas) return "";

  const threshold = intervalMinutes === 5 ? 200000 : 30000;
  const ceVolume = number(row.deltas.ceVolume);
  const peVolume = number(row.deltas.peVolume);
  const ceSellers = number(row.deltas.ceSellers);
  const peSellers = number(row.deltas.peSellers);

  const bullishStrength = Math.max(
    ceVolume >= threshold ? ceVolume : 0,
    peVolume <= -threshold ? Math.abs(peVolume) : 0
  );

  const bearishStrength = Math.max(
    peVolume >= threshold ? peVolume : 0,
    ceVolume <= -threshold ? Math.abs(ceVolume) : 0
  );

  if (!bullishStrength && !bearishStrength) return "";
  if (bullishStrength === bearishStrength) return "";

  if (bearishStrength > bullishStrength && ceSellers > 0) return "ce";
  if (bullishStrength > bearishStrength && peSellers > 0) return "pe";

  return "";
}

function chartTurnReason(previous, current) {
  const ceSellers = number(current.ceWritingOi) - number(previous.ceWritingOi);
  const peSellers = number(current.peWritingOi) - number(previous.peWritingOi);
  const ceVolume = number(current.ceVolume) - number(previous.ceVolume);
  const peVolume = number(current.peVolume) - number(previous.peVolume);
  const optionVolume = ceVolume + peVolume;
  const spotMove = number(current.spot) - number(previous.spot);
  const drivers = [
    { strength: Math.abs(ceSellers), text: `CE sellers ${signed(ceSellers)}` },
    { strength: Math.abs(peSellers), text: `PE sellers ${signed(peSellers)}` },
    { strength: Math.abs(ceVolume), text: `CE vol ${signed(ceVolume)}` },
    { strength: Math.abs(peVolume), text: `PE vol ${signed(peVolume)}` },
  ].sort((a, b) => b.strength - a.strength);

  return {
    short: drivers[0]?.text || "Flow unchanged",
    detail: `Spot ${spotMove >= 0 ? "+" : ""}${fmt(spotMove, 2)} | CE sellers ${signed(ceSellers)} | PE sellers ${signed(peSellers)} | CE vol ${signed(ceVolume)} | PE vol ${signed(peVolume)} | Option Vol ${signed(optionVolume)}`,
  };
}

function stableZoneSeries(points, zoneKey) {
  let activeZone = null;

  return points.map(point => {
    const zones = point[zoneKey] || [];
    const candidate = zones[0];
    if (!candidate) return activeZone;
    if (!activeZone || number(candidate.strike) === number(activeZone.strike)) {
      activeZone = candidate;
      return activeZone;
    }

    const activeAtPoint = zones.find(zone => number(zone.strike) === number(activeZone.strike));
    if (!activeAtPoint || number(candidate.oi) >= number(activeAtPoint.oi) * 1.1) {
      activeZone = candidate;
    } else {
      activeZone = activeAtPoint;
    }
    return activeZone;
  });
}

function ReactionChart({ records, index, date, period }) {
  const [hoveredPosition, setHoveredPosition] = useState(null);
  const [chartLayers, setChartLayers] = useState({
    spot: true,
    zones: true,
    events: true,
  });
  const indexRecords = [...records]
    .filter(record => !index || record.index === index)
    .sort((a, b) => new Date(a.capturedAt) - new Date(b.capturedAt));
  const sheetPoints = indexRecords
    .filter(record =>
      (!date || record.date === date)
      && (!period || record.period === period)
    );
  const datePoints = indexRecords.filter(record => !date || record.date === date);
  const points = sheetPoints.length >= 2
    ? sheetPoints
    : datePoints.length >= 2
      ? datePoints
      : indexRecords.slice(-80);
  const chartScope = sheetPoints.length >= 2
    ? `${date} | ${period}`
    : datePoints.length >= 2
      ? `${date} | Full Session`
      : "Latest Available";

  if (points.length < 2) {
    return (
      <div style={{ marginTop: 14, minHeight: 250, padding: 35, textAlign: "center", color: colors.dimGold, border: `1px solid ${colors.line}`, borderRadius: 8 }}>
        Two or more snapshots are needed for the reaction chart.
      </div>
    );
  }

  const width = 1000;
  const height = 330;
  const padding = 42;
  const resistanceSeries = stableZoneSeries(points, "resistanceZones");
  const supportSeries = stableZoneSeries(points, "supportZones");
  const values = points.flatMap((point, position) => [
    number(point.spot),
    number(resistanceSeries[position]?.strike),
    number(supportSeries[position]?.strike),
  ]).filter(Boolean);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const spread = Math.max(max - min, 1);
  const x = position => padding + (position / Math.max(points.length - 1, 1)) * (width - padding * 2);
  const y = value => height - padding - ((number(value) - min) / spread) * (height - padding * 2);
  const pathFor = valuesForPath => valuesForPath
    .map((value, position) => `${position ? "L" : "M"} ${x(position)} ${y(value)}`)
    .join(" ");
  const turns = points.map((point, position) => {
    if (!position) return null;
    const previous = points[position - 1];
    const resistanceChanged = number(resistanceSeries[position]?.strike) !== number(resistanceSeries[position - 1]?.strike);
    const supportChanged = number(supportSeries[position]?.strike) !== number(supportSeries[position - 1]?.strike);
    const ceSellerChange = number(point.ceWritingOi) - number(previous.ceWritingOi);
    const peSellerChange = number(point.peWritingOi) - number(previous.peWritingOi);
    const materialSellerChange =
      Math.abs(ceSellerChange) >= Math.max(number(previous.ceWritingOi) * 0.05, 1)
      || Math.abs(peSellerChange) >= Math.max(number(previous.peWritingOi) * 0.05, 1);
    if (!resistanceChanged && !supportChanged && !materialSellerChange) return null;
    return {
      point,
      position,
      resistanceChanged,
      supportChanged,
      reason: chartTurnReason(previous, point),
      ceSellerChange,
      peSellerChange,
      resistance: resistanceSeries[position],
      support: supportSeries[position],
    };
  }).filter(Boolean);
  const displayedTurns = turns.slice(-8);
  const lastPosition = points.length - 1;
  const latestPoint = points[lastPosition];
  const latestResistance = resistanceSeries[lastPosition];
  const latestSupport = supportSeries[lastPosition];
  const hoveredPoint = hoveredPosition === null ? null : points[hoveredPosition];
  const hoveredPrevious = hoveredPosition > 0 ? points[hoveredPosition - 1] : null;
  const hoveredData = hoveredPoint ? {
    ceSellers: hoveredPrevious ? number(hoveredPoint.ceWritingOi) - number(hoveredPrevious.ceWritingOi) : 0,
    peSellers: hoveredPrevious ? number(hoveredPoint.peWritingOi) - number(hoveredPrevious.peWritingOi) : 0,
    ceVolume: hoveredPrevious ? number(hoveredPoint.ceVolume) - number(hoveredPrevious.ceVolume) : 0,
    peVolume: hoveredPrevious ? number(hoveredPoint.peVolume) - number(hoveredPrevious.peVolume) : 0,
    resistance: resistanceSeries[hoveredPosition],
    support: supportSeries[hoveredPosition],
  } : null;
  const hoverColumnWidth = (width - padding * 2) / Math.max(points.length - 1, 1);
  const tooltipWidth = 245;
  const tooltipHeight = 122;
  const tooltipX = hoveredPosition === null
    ? 0
    : Math.min(Math.max(x(hoveredPosition) + 12, padding), width - padding - tooltipWidth);
  const tooltipY = hoveredPoint
    ? Math.min(Math.max(y(hoveredPoint.spot) - tooltipHeight - 12, padding), height - padding - tooltipHeight)
    : 0;

  return (
    <div style={{ marginTop: 14, background: colors.panel, border: `1px solid ${colors.line}`, borderRadius: 8, padding: 12 }}>
      <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", gap: 8, color: colors.gold, fontWeight: 900 }}>
        <span>{index} Spot vs Seller Zones | {chartScope}</span>
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12, fontSize: 10 }}>
          {[
            ["spot", "Spot", colors.blue],
            ["zones", "Zones", colors.green],
            ["events", "Events", colors.gold],
          ].map(([key, label, color]) => (
            <label key={key} style={{ display: "flex", alignItems: "center", gap: 5, color, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={chartLayers[key]}
                onChange={event => setChartLayers(current => ({ ...current, [key]: event.target.checked }))}
              />
              {label}
            </label>
          ))}
          <span>
            <span style={{ color: colors.red }}>RED: CE RESISTANCE</span>{" | "}
            <span style={{ color: colors.green }}>GREEN: PE SUPPORT</span>
          </span>
        </div>
      </div>
      <div style={{ overflowX: "auto" }}>
        <svg
          viewBox={`0 0 ${width} ${height}`}
          onMouseLeave={() => setHoveredPosition(null)}
          style={{ width: "100%", minWidth: 820, height: 330, marginTop: 8 }}
        >
          {[0, 0.25, 0.5, 0.75, 1].map(ratio => {
            const lineY = padding + ratio * (height - padding * 2);
            const label = max - ratio * spread;
            return (
              <g key={ratio}>
                <line x1={padding} x2={width - padding} y1={lineY} y2={lineY} stroke="rgba(247,201,72,0.12)" />
                <text x={2} y={lineY + 4} fill={colors.dimGold} fontSize="10">{fmt(label)}</text>
              </g>
            );
          })}
          {chartLayers.zones && (
            <>
              <path d={pathFor(points.map((point, position) => resistanceSeries[position]?.strike || point.spot))} fill="none" stroke={colors.red} strokeWidth="2" />
              <path d={pathFor(points.map((point, position) => supportSeries[position]?.strike || point.spot))} fill="none" stroke={colors.green} strokeWidth="2" />
              {turns.map(turn => (
                <g key={`turn-${turn.point.id}`}>
                  {turn.resistanceChanged && (
                    <circle cx={x(turn.position)} cy={y(turn.resistance?.strike || turn.point.spot)} r="4" fill={colors.red}>
                      <title>{turn.point.time?.slice(0, 5)} resistance changed to {fmt(turn.resistance?.strike)} | CE sellers {signed(turn.ceSellerChange)}</title>
                    </circle>
                  )}
                  {turn.supportChanged && (
                    <circle cx={x(turn.position)} cy={y(turn.support?.strike || turn.point.spot)} r="4" fill={colors.green}>
                      <title>{turn.point.time?.slice(0, 5)} support changed to {fmt(turn.support?.strike)} | PE sellers {signed(turn.peSellerChange)}</title>
                    </circle>
                  )}
                </g>
              ))}
              {latestResistance && (
                <circle cx={x(lastPosition)} cy={y(latestResistance.strike)} r="5" fill={colors.red} stroke="#fff" strokeWidth="1.5" />
              )}
              {latestSupport && (
                <circle cx={x(lastPosition)} cy={y(latestSupport.strike)} r="5" fill={colors.green} stroke="#fff" strokeWidth="1.5" />
              )}
            </>
          )}
          {chartLayers.spot && (
            <>
              <path d={pathFor(points.map(point => point.spot))} fill="none" stroke={colors.blue} strokeWidth="3" />
              <circle cx={x(lastPosition)} cy={y(latestPoint.spot)} r="5" fill={colors.blue} stroke="#fff" strokeWidth="1.5" />
            </>
          )}
          {hoveredPoint && hoveredData && (
            <g style={{ pointerEvents: "none" }}>
              <line
                x1={x(hoveredPosition)}
                x2={x(hoveredPosition)}
                y1={padding}
                y2={height - padding}
                stroke={colors.gold}
                strokeWidth="1"
                strokeDasharray="4 4"
                opacity="0.75"
              />
              {chartLayers.spot && (
                <circle cx={x(hoveredPosition)} cy={y(hoveredPoint.spot)} r="6" fill={colors.blue} stroke="#fff" strokeWidth="1.5" />
              )}
              {chartLayers.zones && hoveredData.resistance && (
                <circle cx={x(hoveredPosition)} cy={y(hoveredData.resistance.strike)} r="5" fill={colors.red} stroke="#fff" strokeWidth="1" />
              )}
              {chartLayers.zones && hoveredData.support && (
                <circle cx={x(hoveredPosition)} cy={y(hoveredData.support.strike)} r="5" fill={colors.green} stroke="#fff" strokeWidth="1" />
              )}
              <foreignObject x={tooltipX} y={tooltipY} width={tooltipWidth} height={tooltipHeight}>
                <div
                  xmlns="http://www.w3.org/1999/xhtml"
                  style={{
                    background: "rgba(5, 5, 5, 0.96)",
                    border: `1px solid ${colors.gold}`,
                    borderRadius: 5,
                    color: "#e5e7eb",
                    fontSize: 10,
                    lineHeight: 1.45,
                    padding: "8px 10px",
                    boxSizing: "border-box",
                  }}
                >
                  <div style={{ color: colors.gold, fontWeight: 900 }}>
                    {hoveredPoint.time?.slice(0, 5)} | Spot {fmt(hoveredPoint.spot, 2)}
                  </div>
                  <div><span style={{ color: colors.red }}>CE sellers:</span> {signed(hoveredData.ceSellers)}</div>
                  <div><span style={{ color: colors.green }}>PE sellers:</span> {signed(hoveredData.peSellers)}</div>
                  <div><span style={{ color: "#fb7185" }}>CE volume:</span> {signed(hoveredData.ceVolume)}</div>
                  <div><span style={{ color: "#a78bfa" }}>PE volume:</span> {signed(hoveredData.peVolume)}</div>
                  <div>
                    <span style={{ color: colors.red }}>Resistance:</span> {fmt(hoveredData.resistance?.strike)}
                    {" | "}
                    <span style={{ color: colors.green }}>Support:</span> {fmt(hoveredData.support?.strike)}
                  </div>
                </div>
              </foreignObject>
            </g>
          )}
          {points.map((point, position) => (
            <rect
              key={`hover-${point.id}`}
              x={Math.max(padding, x(position) - hoverColumnWidth / 2)}
              y={padding}
              width={Math.min(hoverColumnWidth, width - padding - Math.max(padding, x(position) - hoverColumnWidth / 2))}
              height={height - padding * 2}
              fill="transparent"
              onMouseEnter={() => setHoveredPosition(position)}
              onMouseMove={() => setHoveredPosition(position)}
              style={{ cursor: "crosshair" }}
            />
          ))}
        </svg>
      </div>
      {chartLayers.events && (
        <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingTop: 8 }}>
          {displayedTurns.length ? displayedTurns.map(turn => (
            <div key={`note-${turn.point.id}`} title={turn.reason.detail} style={{ flex: "0 0 auto", maxWidth: 250, border: `1px solid ${colors.line}`, borderRadius: 5, padding: "6px 8px", fontSize: 9 }}>
              <strong style={{ color: colors.gold }}>{turn.point.time?.slice(0, 5)}</strong>
              <span style={{ color: colors.blue }}> Spot {fmt(turn.point.spot, 2)}</span>
              <div style={{ color: colors.dimGold, marginTop: 2 }}>
                {turn.resistanceChanged ? `R → ${fmt(turn.resistance?.strike)} | ` : ""}
                {turn.supportChanged ? `S → ${fmt(turn.support?.strike)} | ` : ""}
                CE {signed(turn.ceSellerChange)} | PE {signed(turn.peSellerChange)}
              </div>
            </div>
          )) : <span style={{ color: colors.dimGold, fontSize: 10 }}>No significant seller or stable-zone changes detected in this sheet.</span>}
        </div>
      )}
    </div>
  );
}

export default function DhanMarketReactionGrid({
  secretClientId = "",
  secretAccessToken = "",
  onOpenStrike,
}) {
  const currentWeek = useMemo(() => weekRange(0), []);
  const [history, setHistory] = useState([]);
  const [indexFilter, setIndexFilter] = useState("NIFTY");
  const [fromDate, setFromDate] = useState(addDays(localDate(), -13));
  const [toDate, setToDate] = useState(localDate());
  const [sessionDate, setSessionDate] = useState(localDate());
  const [activePeriod, setActivePeriod] = useState(HOURLY_PERIODS[0]);
  const [studyInterval, setStudyInterval] = useState(1);
  const [selectedId, setSelectedId] = useState("");
  const [status, setStatus] = useState("");
  const [collectorStatus, setCollectorStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hoveredMinutePosition, setHoveredMinutePosition] = useState(null);
  const [credentialsOpen, setCredentialsOpen] = useState(false);
  const [credentials, setCredentials] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("dhan_market_reaction_credentials_v1") || "{}");
      return {
        clientId: saved.clientId || secretClientId,
        accessToken: saved.accessToken || secretAccessToken,
      };
    } catch {
      return { clientId: secretClientId, accessToken: secretAccessToken };
    }
  });
  const [credentialStatus, setCredentialStatus] = useState("");

  useEffect(() => {
    setCredentials(current => ({
      clientId: current.clientId || secretClientId,
      accessToken: current.accessToken || secretAccessToken,
    }));
  }, [secretAccessToken, secretClientId]);

  useEffect(() => {
    const loadAdminCredentials = async () => {
      const token = localStorage.getItem("ct_login_token");
      if (!token) return;
      try {
        const response = await fetch(`${API_BASE}/api/login/admin/secret-settings`, {
          headers: { Authorization: `Bearer ${token}` },
          cache: "no-store",
        });
        const data = await response.json();
        if (!response.ok || !data.success) return;
        setCredentials(current => ({
          clientId: data.settings?.clientId || current.clientId,
          accessToken: data.settings?.accessToken || current.accessToken,
        }));
      } catch {
        // Props and locally saved credentials remain available as fallbacks.
      }
    };
    loadAdminCredentials();
  }, []);

  const saveCredentials = async () => {
    const next = {
      clientId: credentials.clientId.trim(),
      accessToken: credentials.accessToken.trim(),
    };
    if (!hasSecretCredentials(next.clientId, next.accessToken)) {
      setCredentialStatus("Enter the active provider credentials in Secret Page Settings");
      return;
    }
    setCredentialStatus("Saving...");
    try {
      localStorage.setItem("dhan_market_reaction_credentials_v1", JSON.stringify(next));
      const token = localStorage.getItem("ct_login_token");
      const settingsResponse = await fetch(`${API_BASE}/api/login/admin/secret-settings`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(next),
      });
      const settingsData = await settingsResponse.json().catch(() => ({}));
      if (!settingsResponse.ok) throw new Error(settingsData.error || "Credential save failed");

      const collectResponse = await fetch(`${API_BASE}/api/dhan-market-logger/collect`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(next),
      });
      const collectData = await collectResponse.json().catch(() => ({}));
      if (!collectResponse.ok) throw new Error(collectData.error || "Dhan collection failed");
      setCredentialStatus("Saved and Dhan collection started");
      fetchHistory();
    } catch (error) {
      setCredentialStatus(error.message);
    }
  };

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ from: fromDate, to: toDate });
      if (indexFilter !== "ALL") params.set("index", indexFilter);
      const response = await fetch(`${API_BASE}/api/dhan-market-logger/view?${params}`, { cache: "no-store" });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.error || "History fetch failed");
      setHistory(data.records || []);
      setSelectedId(current => current || data.records?.[0]?.id || "");
      setStatus(`${data.records?.length || 0} snapshots loaded`);
    } catch (error) {
      setStatus(error.message);
    } finally {
      setLoading(false);
    }
  }, [fromDate, indexFilter, toDate]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  useEffect(() => {
    const loadCollectorStatus = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/dhan-market-logger/status`, { cache: "no-store" });
        const data = await response.json();
        if (response.ok && data.success) setCollectorStatus(data);
      } catch {
        setCollectorStatus(null);
      }
    };
    loadCollectorStatus();
    const interval = setInterval(() => {
      fetchHistory();
      loadCollectorStatus();
    }, 15000);
    return () => clearInterval(interval);
  }, [fetchHistory]);

  const selected = history.find(record => record.id === selectedId) || history[0];
  const latest = history[0];
  const studyIndex = indexFilter === "ALL" ? selected?.index || "NIFTY" : indexFilter;
  const hourlyStudy = useMemo(
    () => periodStudy(history, sessionDate, studyIndex, activePeriod),
    [activePeriod, history, sessionDate, studyIndex]
  );
  const studyLedger = useMemo(
  () => studyInterval === 1
    ? buildMinuteLedger(history, sessionDate, studyIndex, activePeriod)
    : buildIntervalLedger(history, sessionDate, studyIndex, activePeriod, studyInterval),
  [activePeriod, history, sessionDate, studyIndex, studyInterval]
);
  const studyPriceSnapshots = useMemo(
    () => history.filter(row => row.date === sessionDate && row.index === studyIndex),
    [history, sessionDate, studyIndex]
  );
  const latestForStudyIndex = history.find(row => row.index === studyIndex) || null;
  const minuteStudyLatest =
    [...studyLedger].reverse().find(row => row.open)?.open ||
    hourlyStudy?.last ||
    latestForStudyIndex;
  const currentStudyFunds = useMemo(() => ({
    ceSellers: sellerMargin(minuteStudyLatest, "ce"),
    peSellers: sellerMargin(minuteStudyLatest, "pe"),
    ceVolume: volumeFunds(minuteStudyLatest, "ce", studyPriceSnapshots),
    peVolume: volumeFunds(minuteStudyLatest, "pe", studyPriceSnapshots),
  }), [minuteStudyLatest, studyPriceSnapshots]);
  const minuteRows = useMemo(() => studyLedger.map(row => ({
    ...row,
    funds: {
      ceSellers: sellerMargin(row.open, "ce"),
      peSellers: sellerMargin(row.open, "pe"),
      ceSellerChange: sellerMarginChange(row.open, row.close, "ce"),
      peSellerChange: sellerMarginChange(row.open, row.close, "pe"),
      ceVolume: volumeFunds(row.open, "ce", studyPriceSnapshots),
      peVolume: volumeFunds(row.open, "pe", studyPriceSnapshots),
      ceVolumeChange: volumeFundsChange(row.open, row.close, "ce", studyPriceSnapshots),
      peVolumeChange: volumeFundsChange(row.open, row.close, "pe", studyPriceSnapshots),
    },
  })), [studyLedger, studyPriceSnapshots]);
  const sheetEndSnapshot = [...minuteRows].reverse().find(row => row.close || row.open);
  const sheetEnd = sheetEndSnapshot?.close || sheetEndSnapshot?.open || null;
  const sheetEndFunds = useMemo(() => ({
    ceSellers: sellerMargin(sheetEnd, "ce"),
    peSellers: sellerMargin(sheetEnd, "pe"),
    ceVolume: volumeFunds(sheetEnd, "ce", studyPriceSnapshots),
    peVolume: volumeFunds(sheetEnd, "pe", studyPriceSnapshots),
  }), [sheetEnd, studyPriceSnapshots]);
  const sheetTotals = useMemo(() => minuteRows.reduce((totals, row) => {
    if (row.open) {
      totals.rows += 1;
    }
    if (row.deltas) {
      totals.ceSellerChange += number(row.deltas.ceSellers);
      totals.peSellerChange += number(row.deltas.peSellers);
      totals.ceVolumeChange += number(row.deltas.ceVolume);
      totals.peVolumeChange += number(row.deltas.peVolume);
      totals.ceOiChange += number(row.deltas.ceOi);
      totals.peOiChange += number(row.deltas.peOi);
      totals.ceSellerFundsChange += number(row.funds.ceSellerChange);
      totals.peSellerFundsChange += number(row.funds.peSellerChange);
      totals.ceVolumeFundsChange += number(row.funds.ceVolumeChange);
      totals.peVolumeFundsChange += number(row.funds.peVolumeChange);
    }
    if (row.close) totals.indexMove += number(row.move);
    return totals;
  }, {
    rows: 0,
    indexMove: 0,
    ceSellerChange: 0,
    peSellerChange: 0,
    ceVolumeChange: 0,
    peVolumeChange: 0,
    ceOiChange: 0,
    peOiChange: 0,
    ceSellerFundsChange: 0,
    peSellerFundsChange: 0,
    ceVolumeFundsChange: 0,
    peVolumeFundsChange: 0,
  }), [minuteRows]);
  const latestPopulatedPosition = useMemo(() => {
    for (let position = minuteRows.length - 1; position >= 0; position -= 1) {
      if (minuteRows[position].open) return position;
    }
    return -1;
  }, [minuteRows]);
  const cumulativePosition =
    hoveredMinutePosition !== null && minuteRows[hoveredMinutePosition]?.open
      ? hoveredMinutePosition
      : latestPopulatedPosition;
  const cumulativeRow = cumulativePosition >= 0 ? minuteRows[cumulativePosition] : null;
  const cumulativeEnd = cumulativeRow?.close || cumulativeRow?.open || null;
  const cumulativeEndFunds = useMemo(() => ({
    ceSellers: sellerMargin(cumulativeEnd, "ce"),
    peSellers: sellerMargin(cumulativeEnd, "pe"),
    ceVolume: volumeFunds(cumulativeEnd, "ce", studyPriceSnapshots),
    peVolume: volumeFunds(cumulativeEnd, "pe", studyPriceSnapshots),
  }), [cumulativeEnd, studyPriceSnapshots]);
  const cumulativeTotals = useMemo(() => {
    if (cumulativePosition < 0) return null;

    return minuteRows.slice(0, cumulativePosition + 1).reduce((totals, row) => {
      if (row.open) totals.rows += 1;
      if (row.deltas) {
        totals.ceSellerChange += number(row.deltas.ceSellers);
        totals.peSellerChange += number(row.deltas.peSellers);
        totals.ceVolumeChange += number(row.deltas.ceVolume);
        totals.peVolumeChange += number(row.deltas.peVolume);
        totals.ceOiChange += number(row.deltas.ceOi);
        totals.peOiChange += number(row.deltas.peOi);
        totals.ceSellerFundsChange += number(row.funds.ceSellerChange);
        totals.peSellerFundsChange += number(row.funds.peSellerChange);
        totals.ceVolumeFundsChange += number(row.funds.ceVolumeChange);
        totals.peVolumeFundsChange += number(row.funds.peVolumeChange);
      }
      if (row.close) totals.indexMove += number(row.move);
      return totals;
    }, {
      rows: 0,
      indexMove: 0,
      ceSellerChange: 0,
      peSellerChange: 0,
      ceVolumeChange: 0,
      peVolumeChange: 0,
      ceOiChange: 0,
      peOiChange: 0,
      ceSellerFundsChange: 0,
      peSellerFundsChange: 0,
      ceVolumeFundsChange: 0,
      peVolumeFundsChange: 0,
    });
  }, [cumulativePosition, minuteRows]);
  useEffect(() => {
    setHoveredMinutePosition(null);
  }, [activePeriod, sessionDate, studyIndex, studyInterval]);
  const latestCompletedRow = [...minuteRows].reverse().find(row => row.close);
  const latestCloseAge = latestCompletedRow?.close?.capturedAt
    ? Date.now() - new Date(latestCompletedRow.close.capturedAt).getTime()
    : Infinity;
  const blinkingRowLabel = sessionDate === localDate()
    && latestCompletedRow?.close?.period === activePeriod
    && latestCloseAge <= 180000
    ? latestCompletedRow.label
    : "";
  const availableDates = useMemo(
    () => [...new Set(history.filter(row => row.index === studyIndex).map(row => row.date))].sort().reverse(),
    [history, studyIndex]
  );
  const currentWeekCount = history.filter(row => row.date >= currentWeek.from && row.date <= currentWeek.to).length;
  const previousWeek = useMemo(() => weekRange(-1), []);
  const previousWeekCount = history.filter(row => row.date >= previousWeek.from && row.date <= previousWeek.to).length;

  const applyWeek = range => {
    setFromDate(range.from);
    setToDate(range.to);
  };

  const exportCsv = () => {
    const headings = [
      "Date", "Sheet", "Minute", "Index", "Index Close", "Index Move",
      "Total Call Sellers", "CE Sellers Change", "Total Put Sellers", "PE Sellers Change",
      "Total CE Volume", "CE Volume Change", "Total PE Volume", "PE Volume Change",
      "Total CE OI", "CE OI Change", "Total PE OI", "PE OI Change",
      "Impact", "One Minute Candle", "Reason",
    ];
    const exportRows = HOURLY_PERIODS.flatMap((period, sheetIndex) =>
      (
        studyInterval === 1
          ? buildMinuteLedger(history, sessionDate, studyIndex, period)
          : buildIntervalLedger(
              history,
              sessionDate,
              studyIndex,
              period,
              studyInterval
            )
      ).map(row => ({
        ...row,
        period,
        sheet: sheetIndex + 1,
      }))
    );
    const lines = exportRows.map(row => [
      sessionDate, `Sheet ${row.sheet}`, row.label, studyIndex,
      row.open?.spot ?? "", row.move ?? "",
      row.open?.ceWritingOi ?? "", row.deltas?.ceSellers ?? "",
      row.open?.peWritingOi ?? "", row.deltas?.peSellers ?? "",
      row.open?.ceVolume ?? "", row.deltas?.ceVolume ?? "",
      row.open?.peVolume ?? "", row.deltas?.peVolume ?? "",
      row.open?.ceOi ?? "", row.deltas?.ceOi ?? "",
      row.open?.peOi ?? "", row.deltas?.peOi ?? "",
      row.close?.reaction ?? "", row.candle ?? "", row.reason ?? "",
    ].map(csvCell).join(","));
    const blob = new Blob([`\uFEFF${headings.map(csvCell).join(",")}\n${lines.join("\n")}`], {
      type: "text/csv;charset=utf-8",
    });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download =
  `dhan-market-reaction-${studyIndex}-${sessionDate}-${studyInterval}-minute-study.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const th = {
    padding: "10px 8px",
    borderBottom: `1px solid ${colors.line}`,
    color: colors.gold,
    textAlign: "right",
    whiteSpace: "nowrap",
    position: "sticky",
    top: 0,
    background: "#090702",
  };
  const td = {
    padding: "9px 8px",
    borderBottom: "1px solid rgba(247,201,72,0.12)",
    textAlign: "right",
    whiteSpace: "nowrap",
  };
  const button = {
    border: `1px solid ${colors.line}`,
    borderRadius: 5,
    background: colors.card,
    color: colors.gold,
    padding: "8px 12px",
    fontWeight: 800,
    cursor: "pointer",
  };

  useSecretBroker(({ clientId, accessToken }) => {
    setCredentials(current => ({ ...current, clientId, accessToken }));
  });

  return (
    <div style={{ minHeight: "100vh", background: colors.background, color: "#e5e7eb", padding: 18, fontFamily: "'JetBrains Mono', monospace" }}>
      <style>{`
        @keyframes currentSellerChangeBlink {
          0%, 100% { background: transparent; box-shadow: none; }
          50% { background: rgba(247, 201, 72, 0.2); box-shadow: inset 0 0 0 1px rgba(247, 201, 72, 0.75); }
        }
        .current-seller-change {
          animation: currentSellerChangeBlink 0.9s ease-in-out infinite;
        }
      `}</style>
      <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", gap: 14, alignItems: "center" }}>
        <div>
          <div style={{ color: colors.dimGold, fontSize: 11, letterSpacing: "0.25em", fontWeight: 800 }}>NEXOVATE MARKET STUDY</div>
          <h1 style={{ color: colors.gold, margin: "6px 0" }}>Dhan Market Reaction</h1>
          <div style={{ color: colors.dimGold, fontSize: 12 }}>Dhan option-chain CE resistance, PE support and index reaction study</div>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          <button
            style={button}
            onClick={() => onOpenStrike ? onOpenStrike() : window.location.assign("/dhan-strike-market-reaction")}
          >
            Dhan Strike Reaction
          </button>
          <button style={button} onClick={() => setCredentialsOpen(value => !value)}>
            Credentials <ChevronDown size={14} style={{ display: "inline", marginLeft: 6 }} />
          </button>
          <button style={button} onClick={fetchHistory}><RefreshCw size={14} style={{ display: "inline", marginRight: 6 }} />Refresh</button>
          <button style={{ ...button, color: colors.green }} onClick={exportCsv} disabled={!history.length}><Download size={14} style={{ display: "inline", marginRight: 6 }} />Export Excel CSV</button>
        </div>
      </div>

      {credentialsOpen && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12, padding: 12, background: colors.panel, border: `1px solid ${colors.line}`, borderRadius: 8 }}>
          <input
            value={credentials.clientId}
            onChange={event => setCredentials(current => ({ ...current, clientId: event.target.value }))}
            placeholder={secretIdLabel()}
            autoComplete="off"
            style={{ ...button, minWidth: 210 }}
          />
          <input
            type="password"
            value={credentials.accessToken}
            onChange={event => setCredentials(current => ({ ...current, accessToken: event.target.value }))}
            placeholder="Access Token"
            autoComplete="new-password"
            style={{ ...button, minWidth: 360 }}
          />
          <button style={{ ...button, color: colors.green }} onClick={saveCredentials}>
            <Save size={14} style={{ display: "inline", marginRight: 6 }} />Save
          </button>
          <span style={{ alignSelf: "center", color: credentialStatus.toLowerCase().includes("failed") || credentialStatus.toLowerCase().includes("required") ? colors.red : colors.dimGold, fontSize: 11 }}>
            {credentialStatus || "Auto-filled from AdminDashboard Secret Page Credentials"}
          </span>
        </div>
      )}

      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 18, padding: 12, background: colors.panel, border: `1px solid ${colors.line}`, borderRadius: 8 }}>
        <select value={indexFilter} onChange={event => setIndexFilter(event.target.value)} style={button}>
          {INDEXES.map(index => <option key={index}>{index}</option>)}
        </select>
        <input type="date" value={fromDate} onChange={event => setFromDate(event.target.value)} style={button} />
        <input type="date" value={toDate} onChange={event => setToDate(event.target.value)} style={button} />
        <button style={button} onClick={() => applyWeek(currentWeek)}>Current Week</button>
        <button style={button} onClick={() => applyWeek(previousWeek)}>Past Week</button>
        <button style={button} onClick={() => { setFromDate(addDays(localDate(), -13)); setToDate(localDate()); }}>Last 14 Days</button>
        <span style={{ marginLeft: "auto", alignSelf: "center", color: status.toLowerCase().includes("failed") ? colors.red : colors.dimGold, fontSize: 12 }}>
          {loading ? "Loading..." : status}
        </span>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(175px, 1fr))", gap: 10, marginTop: 12 }}>
        <SummaryCard label="LATEST SPOT" value={latest ? fmt(latest.spot, 2) : "--"} color={colors.blue} detail={latest?.index} />
        <SummaryCard label="CE SELLERS" value={latest ? fmt(latest.ceSellers) : "--"} color={colors.red} detail="Resistance-side OI" />
        <SummaryCard label="PE SELLERS" value={latest ? fmt(latest.peSellers) : "--"} color={colors.green} detail="Support-side OI" />
        <SummaryCard label="SELLER BIAS" value={latest?.sellerBias || "--"} color={latest?.sellerBias === "PUT HEAVY" ? colors.green : colors.red} detail={latest?.reaction} />
        <SummaryCard label="CURRENT WEEK" value={currentWeekCount} detail={`${currentWeek.from} to ${currentWeek.to}`} />
        <SummaryCard label="PAST WEEK" value={previousWeekCount} detail={`${previousWeek.from} to ${previousWeek.to}`} />
      </div>

      <div style={{ marginTop: 14, background: colors.panel, border: `1px solid ${colors.line}`, borderRadius: 8, padding: 12 }}>
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
          <strong style={{ color: colors.gold }}>HOURLY SESSION SHEETS</strong>
          <select value={sessionDate} onChange={event => setSessionDate(event.target.value)} style={{ ...button, marginLeft: "auto" }}>
            {!availableDates.includes(sessionDate) && <option value={sessionDate}>{sessionDate}</option>}
            {availableDates.map(date => <option key={date} value={date}>{date}</option>)}
          </select>
        </div>
        <div style={{ display: "flex", gap: 7, overflowX: "auto", padding: "12px 0" }}>
          {HOURLY_PERIODS.map((period, position) => (
            <button
              key={period}
              onClick={() => setActivePeriod(period)}
              style={{
                ...button,
                flex: "0 0 auto",
                background: activePeriod === period ? "#006b49" : colors.card,
                color: activePeriod === period ? "#fff" : colors.gold,
                borderColor: activePeriod === period ? colors.green : colors.line,
              }}
            >
              Sheet {position + 1}<br /><span style={{ fontSize: 10 }}>{period}</span>
            </button>
          ))}
        </div>

        {hourlyStudy ? (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 8 }}>
              <SummaryCard label="INDEX CANDLE" value={`${fmt(hourlyStudy.open, 2)} → ${fmt(hourlyStudy.close, 2)}`} color={hourlyStudy.move >= 0 ? colors.green : colors.red} detail={`H ${fmt(hourlyStudy.high, 2)} | L ${fmt(hourlyStudy.low, 2)} | ${hourlyStudy.move >= 0 ? "+" : ""}${fmt(hourlyStudy.move, 2)}`} />
              <SummaryCard label="CE WRITING ADDED" value={fmt(hourlyStudy.ceWritingAdded)} color={colors.red} detail={`CE OI net ${fmt(hourlyStudy.ceOiChange)}`} />
              <SummaryCard label="PE WRITING ADDED" value={fmt(hourlyStudy.peWritingAdded)} color={colors.green} detail={`PE OI net ${fmt(hourlyStudy.peOiChange)}`} />
              <SummaryCard label="CE BUYING ADDED" value={fmt(hourlyStudy.ceBuyingAdded)} color="#fb7185" detail="Long buildup OI" />
              <SummaryCard label="PE BUYING ADDED" value={fmt(hourlyStudy.peBuyingAdded)} color="#a78bfa" detail="Long buildup OI" />
              <SummaryCard label="INSTITUTIONAL READ" value={hourlyStudy.impact} color={colors.gold} detail={`${hourlyStudy.snapshots.length} five-minute observations`} />
            </div>
            <div style={{ marginTop: 10, color: colors.dimGold, fontSize: 11 }}>
              Interval volume added: CE {fmt(hourlyStudy.ceVolumeChange)} | PE {fmt(hourlyStudy.peVolumeChange)} | Resistance {zoneText(hourlyStudy.last.resistanceZones)} | Support {zoneText(hourlyStudy.last.supportZones)}
            </div>
          </>
        ) : (
          <div style={{ padding: 28, textAlign: "center", color: colors.dimGold }}>
            No observations saved for {studyIndex}, {sessionDate}, {activePeriod}.
          </div>
        )}
      </div>

      <ReactionChart records={history} index={studyIndex} date={sessionDate} period={activePeriod} />

      <div style={{ marginTop: 14, background: colors.panel, border: `1px solid ${colors.line}`, borderRadius: 8, maxHeight: 650, overflow: "auto" }}>
        <div style={{ padding: 12, display: "flex", flexWrap: "wrap", alignItems: "flex-start", gap: 12 }}>
          <div style={{ color: colors.gold, fontWeight: 900, paddingTop: 8, minWidth: 360 }}>
            Minute Study: {studyIndex} | {sessionDate} | Sheet {HOURLY_PERIODS.indexOf(activePeriod) + 1} | {activePeriod}
            <div style={{ color: colors.dimGold, fontSize: 9, marginTop: 5 }}>
              Sellers: 1 ITM + ATM + 10 OTM | Volume/OI: Top 10 ITM
            </div>
            <div style={{ display: "flex", gap: 5, marginTop: 9 }}>
              {[
                [1, "1 Min"],
                [5, "5 Min"],
              ].map(([interval, label]) => (
                <button
                  key={interval}
                  type="button"
                  onClick={() => setStudyInterval(interval)}
                  style={{
                    ...button,
                    padding: "5px 10px",
                    fontSize: 10,
                    background: studyInterval === interval ? "#006b49" : colors.card,
                    color: studyInterval === interval ? "#fff" : colors.gold,
                    borderColor: studyInterval === interval ? colors.green : colors.line,
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div style={{ marginLeft: "auto", display: "grid", gridTemplateColumns: "repeat(3, minmax(165px, 1fr))", gap: 6 }}>
            <StudyMetric label="TOTAL CE SELLERS" value={minuteStudyLatest ? fmt(minuteStudyLatest.ceWritingOi ?? minuteStudyLatest.ceSellers) : "--"} color={colors.red} definition="CE OI: 1 ITM + ATM + 10 OTM" funds={minuteStudyLatest ? currentStudyFunds.ceSellers : undefined} fundsLabel="total margin" />
            <StudyMetric label="TOTAL PE SELLERS" value={minuteStudyLatest ? fmt(minuteStudyLatest.peWritingOi ?? minuteStudyLatest.peSellers) : "--"} color={colors.green} definition="PE OI: 10 OTM + ATM + 1 ITM" funds={minuteStudyLatest ? currentStudyFunds.peSellers : undefined} fundsLabel="total margin" />
            <StudyMetric label="TOTAL CE VOLUME" value={minuteStudyLatest ? fmt(minuteStudyLatest.ceVolume) : "--"} color="#fb7185" definition="Top 10 ITM CE contracts" funds={minuteStudyLatest ? currentStudyFunds.ceVolume : undefined} fundsLabel="premium funds" />
            <StudyMetric label="TOTAL PE VOLUME" value={minuteStudyLatest ? fmt(minuteStudyLatest.peVolume) : "--"} color="#a78bfa" definition="Top 10 ITM PE contracts" funds={minuteStudyLatest ? currentStudyFunds.peVolume : undefined} fundsLabel="premium funds" />
            <StudyMetric label="TOTAL CE OPEN INTEREST" value={minuteStudyLatest ? fmt(minuteStudyLatest.ceOi) : "--"} color={colors.red} definition="Top 10 ITM CE contracts" />
            <StudyMetric label="TOTAL PE OPEN INTEREST" value={minuteStudyLatest ? fmt(minuteStudyLatest.peOi) : "--"} color={colors.green} definition="Top 10 ITM PE contracts" />
          </div>
        </div>
        {!studyLedger.some(row => row.open) && (
          <div style={{ margin: "0 12px 12px", padding: 10, border: `1px solid ${colors.line}`, color: colors.dimGold, fontSize: 12 }}>
            No market-hours snapshots exist for this sheet. The backend must remain running from 09:15 to 15:45 IST.
            {" "}Collector: {collectorStatus ? `connected to Dhan, every ${collectorStatus.intervalMinutes} minute` : "not reachable"}.
            {collectorStatus?.lastCollectionError ? <div style={{ color: colors.red, marginTop: 5 }}>Fetch error: {collectorStatus.lastCollectionError}</div> : null}
          </div>
        )}
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
          <thead>
            <tr>
              {[
                "#",
                `${studyInterval === 1 ? "ONE" : "FIVE"} MINUTE`,
                `${studyIndex} SPOT`,
                "CALL SELLERS",
                "CE SELLERS +/-",
                "PUT SELLERS",
                "PE SELLERS +/-",
                "CE VOLUME",
                "CE VOL +/-",
                "PE VOLUME",
                "PE VOL +/-",
                "CE OI",
                "CE OI +/-",
                "PE OI",
                "PE OI +/-",
                "IMPACT ON INDEX",
                `${studyInterval} MIN CANDLE / REASON`,
              ].map((label, index) => (
                <th key={label} style={{ ...th, textAlign: index < 2 || index > 14 ? "left" : "right" }}>
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody onMouseLeave={() => setHoveredMinutePosition(null)}>
            {minuteRows.map((row, position) => (
              <React.Fragment key={row.label}>
                <tr
                      onMouseEnter={() => row.open && setHoveredMinutePosition(position)}
                      style={{
                        background:
                          volumeRowHighlight(row, studyInterval) ||
                          (row.open ? "transparent" : "rgba(255,255,255,0.015)"),
                      }}
                    >
                  <td style={{ ...td, textAlign: "left", color: colors.dimGold }}>{row.serial}</td>
                  <td style={{ ...td, textAlign: "left", color: colors.gold, fontWeight: 900 }}>{row.label}</td>
                  <td style={{ ...td, color: colors.blue, fontWeight: 900 }}>{row.open ? fmt(row.open.spot, 2) : "--"}</td>
                  <td style={{ ...td, color: colors.red }}>
                    {row.open ? <><div>{fmt(row.open.ceWritingOi)}</div><CroreDetail value={row.funds.ceSellers} /></> : "--"}
                  </td>
                  <td
                    className={
                      sellerConfirmationField(row, studyInterval) === "ce" ||
                      (row.label === blinkingRowLabel && number(row.deltas?.ceSellers) !== 0)
                        ? "current-seller-change"
                        : ""
                    }
                    style={{ ...td, color: number(row.deltas?.ceSellers) <= 0 ? colors.green : colors.red }}
                  >
                    {row.deltas ? <><div>{row.deltas.ceSellers >= 0 ? "+" : ""}{fmt(row.deltas.ceSellers)}</div><CroreDetail value={row.funds.ceSellerChange} change changeQuantity={row.deltas.ceSellers} /></> : "--"}
                  </td>
                  <td style={{ ...td, color: colors.green }}>
                    {row.open ? <><div>{fmt(row.open.peWritingOi)}</div><CroreDetail value={row.funds.peSellers} /></> : "--"}
                  </td>
                  <td
                    className={
                      sellerConfirmationField(row, studyInterval) === "pe" ||
                      (row.label === blinkingRowLabel && number(row.deltas?.peSellers) !== 0)
                        ? "current-seller-change"
                        : ""
                    }
                    style={{ ...td, color: number(row.deltas?.peSellers) >= 0 ? colors.green : colors.red }}
                  >
                    {row.deltas ? <><div>{row.deltas.peSellers >= 0 ? "+" : ""}{fmt(row.deltas.peSellers)}</div><CroreDetail value={row.funds.peSellerChange} change changeQuantity={row.deltas.peSellers} /></> : "--"}
                  </td>
                  <td style={td}>
                    {row.open ? <><div>{fmt(row.open.ceVolume)}</div><CroreDetail value={row.funds.ceVolume} label="premium funds" /></> : "--"}
                  </td>
                  <td
                    className={volumeHighlightField(row, studyInterval) === "ce" ? "current-seller-change" : ""}
                    style={td}
                  >
                    {row.deltas ? <><div>{row.deltas.ceVolume >= 0 ? "+" : ""}{fmt(row.deltas.ceVolume)}</div><CroreDetail value={row.funds.ceVolumeChange} change changeQuantity={row.deltas.ceVolume} /></> : "--"}
                  </td>
                  <td style={td}>
                    {row.open ? <><div>{fmt(row.open.peVolume)}</div><CroreDetail value={row.funds.peVolume} label="premium funds" /></> : "--"}
                  </td>
                  <td
                    className={volumeHighlightField(row, studyInterval) === "pe" ? "current-seller-change" : ""}
                    style={td}
                  >
                    {row.deltas ? <><div>{row.deltas.peVolume >= 0 ? "+" : ""}{fmt(row.deltas.peVolume)}</div><CroreDetail value={row.funds.peVolumeChange} change changeQuantity={row.deltas.peVolume} /></> : "--"}
                  </td>
                  <td style={td}>{row.open ? fmt(row.open.ceOi) : "--"}</td>
                  <td style={td}>{row.deltas ? `${row.deltas.ceOi >= 0 ? "+" : ""}${fmt(row.deltas.ceOi)}` : "--"}</td>
                  <td style={td}>{row.open ? fmt(row.open.peOi) : "--"}</td>
                  <td style={td}>{row.deltas ? `${row.deltas.peOi >= 0 ? "+" : ""}${fmt(row.deltas.peOi)}` : "--"}</td>
                  <td style={{ ...td, textAlign: "left", color: row.move >= 0 ? colors.green : colors.red }}>
                    {row.close
                      ? `${row.move >= 0 ? "+" : ""}${fmt(row.move, 2)} pts`
                      : row.open
                        ? `Awaiting ${studyInterval === 1 ? "next minute" : "interval close"}`
                        : "Awaiting data"}
                  </td>
                  <td style={{ ...td, textAlign: "left", fontWeight: 800, color: row.candle === "BULLISH" ? colors.green : row.candle === "BEARISH" ? colors.red : colors.gold }}>
                    {row.close ? `${row.candle} - ${row.reason}` : "--"}
                  </td>
                </tr>
                {position === cumulativePosition && cumulativeTotals && cumulativeEnd && (
                  <tr
                    style={{
                      background: "rgba(56,189,248,0.14)",
                      color: "#bae6fd",
                      fontWeight: 900,
                      boxShadow: "inset 0 1px 0 rgba(56,189,248,0.55), inset 0 -1px 0 rgba(56,189,248,0.55)",
                    }}
                  >
                    <td style={{ ...td, textAlign: "left", color: colors.blue }}>Σ</td>
                    <td style={{ ...td, textAlign: "left", color: colors.blue }}>TOTAL THROUGH {row.label}</td>
                    <td style={{ ...td, color: colors.blue }}>{fmt(cumulativeEnd.spot, 2)}</td>
                    <td style={{ ...td, color: colors.red }}>
                      <div>{fmt(cumulativeEnd.ceWritingOi)}</div>
                      <CroreDetail value={cumulativeEndFunds.ceSellers} label="left till this minute" />
                    </td>
                    <td style={{ ...td, color: cumulativeTotals.ceSellerChange <= 0 ? colors.green : colors.red }}>
                      <div>{signed(cumulativeTotals.ceSellerChange)}</div>
                      <CroreDetail value={cumulativeTotals.ceSellerFundsChange} change changeQuantity={cumulativeTotals.ceSellerChange} />
                    </td>
                    <td style={{ ...td, color: colors.green }}>
                      <div>{fmt(cumulativeEnd.peWritingOi)}</div>
                      <CroreDetail value={cumulativeEndFunds.peSellers} label="left till this minute" />
                    </td>
                    <td style={{ ...td, color: cumulativeTotals.peSellerChange >= 0 ? colors.green : colors.red }}>
                      <div>{signed(cumulativeTotals.peSellerChange)}</div>
                      <CroreDetail value={cumulativeTotals.peSellerFundsChange} change changeQuantity={cumulativeTotals.peSellerChange} />
                    </td>
                    <td style={td}>
                      <div>{fmt(cumulativeEnd.ceVolume)}</div>
                      <CroreDetail value={cumulativeEndFunds.ceVolume} label="left till this minute" />
                    </td>
                    <td style={td}>
                      <div>{signed(cumulativeTotals.ceVolumeChange)}</div>
                      <CroreDetail value={cumulativeTotals.ceVolumeFundsChange} change changeQuantity={cumulativeTotals.ceVolumeChange} />
                    </td>
                    <td style={td}>
                      <div>{fmt(cumulativeEnd.peVolume)}</div>
                      <CroreDetail value={cumulativeEndFunds.peVolume} label="left till this minute" />
                    </td>
                    <td style={td}>
                      <div>{signed(cumulativeTotals.peVolumeChange)}</div>
                      <CroreDetail value={cumulativeTotals.peVolumeFundsChange} change changeQuantity={cumulativeTotals.peVolumeChange} />
                    </td>
                    <td style={td}>{fmt(cumulativeEnd.ceOi)}</td>
                    <td style={td}>{signed(cumulativeTotals.ceOiChange)}</td>
                    <td style={td}>{fmt(cumulativeEnd.peOi)}</td>
                    <td style={td}>{signed(cumulativeTotals.peOiChange)}</td>
                    <td style={{ ...td, textAlign: "left" }}>{signed(cumulativeTotals.indexMove)} pts</td>
                    <td style={{ ...td, textAlign: "left", color: colors.blue }}>{cumulativeTotals.rows} populated minutes</td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ background: "#120f06", color: colors.gold, fontWeight: 900 }}>
              <td style={{ ...td, textAlign: "left", borderTop: `2px solid ${colors.gold}` }} />
              <td style={{ ...td, textAlign: "left", borderTop: `2px solid ${colors.gold}` }}>TOTAL</td>
              <td style={{ ...td, color: colors.blue, borderTop: `2px solid ${colors.gold}` }}>{sheetEnd ? fmt(sheetEnd.spot, 2) : "--"}</td>
              <td style={{ ...td, color: colors.red, borderTop: `2px solid ${colors.gold}` }}>
                <div>{sheetEnd ? fmt(sheetEnd.ceWritingOi) : "--"}</div>
                {sheetEnd && <CroreDetail value={sheetEndFunds.ceSellers} label="left at sheet end" />}
              </td>
              <td style={{ ...td, borderTop: `2px solid ${colors.gold}`, color: sheetTotals.ceSellerChange <= 0 ? colors.green : colors.red }}>
                <div>{signed(sheetTotals.ceSellerChange)}</div>
                <CroreDetail value={sheetTotals.ceSellerFundsChange} change changeQuantity={sheetTotals.ceSellerChange} />
              </td>
              <td style={{ ...td, color: colors.green, borderTop: `2px solid ${colors.gold}` }}>
                <div>{sheetEnd ? fmt(sheetEnd.peWritingOi) : "--"}</div>
                {sheetEnd && <CroreDetail value={sheetEndFunds.peSellers} label="left at sheet end" />}
              </td>
              <td style={{ ...td, borderTop: `2px solid ${colors.gold}`, color: sheetTotals.peSellerChange >= 0 ? colors.green : colors.red }}>
                <div>{signed(sheetTotals.peSellerChange)}</div>
                <CroreDetail value={sheetTotals.peSellerFundsChange} change changeQuantity={sheetTotals.peSellerChange} />
              </td>
              <td style={{ ...td, borderTop: `2px solid ${colors.gold}` }}>
                <div>{sheetEnd ? fmt(sheetEnd.ceVolume) : "--"}</div>
                {sheetEnd && <CroreDetail value={sheetEndFunds.ceVolume} label="left at sheet end" />}
              </td>
              <td style={{ ...td, borderTop: `2px solid ${colors.gold}` }}>
                <div>{signed(sheetTotals.ceVolumeChange)}</div>
                <CroreDetail value={sheetTotals.ceVolumeFundsChange} change changeQuantity={sheetTotals.ceVolumeChange} />
              </td>
              <td style={{ ...td, borderTop: `2px solid ${colors.gold}` }}>
                <div>{sheetEnd ? fmt(sheetEnd.peVolume) : "--"}</div>
                {sheetEnd && <CroreDetail value={sheetEndFunds.peVolume} label="left at sheet end" />}
              </td>
              <td style={{ ...td, borderTop: `2px solid ${colors.gold}` }}>
                <div>{signed(sheetTotals.peVolumeChange)}</div>
                <CroreDetail value={sheetTotals.peVolumeFundsChange} change changeQuantity={sheetTotals.peVolumeChange} />
              </td>
              <td style={{ ...td, borderTop: `2px solid ${colors.gold}` }}>{sheetEnd ? fmt(sheetEnd.ceOi) : "--"}</td>
              <td style={{ ...td, borderTop: `2px solid ${colors.gold}` }}>{signed(sheetTotals.ceOiChange)}</td>
              <td style={{ ...td, borderTop: `2px solid ${colors.gold}` }}>{sheetEnd ? fmt(sheetEnd.peOi) : "--"}</td>
              <td style={{ ...td, borderTop: `2px solid ${colors.gold}` }}>{signed(sheetTotals.peOiChange)}</td>
              <td style={{ ...td, textAlign: "left", borderTop: `2px solid ${colors.gold}` }}>{signed(sheetTotals.indexMove)} pts</td>
              <td style={{ ...td, textAlign: "left", borderTop: `2px solid ${colors.gold}` }}>{sheetTotals.rows} populated minutes</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
